import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery } from '@apollo/client/react';
import { GET_ATTRIBUTES } from '@features/products/graphql/queries';
import { GetAttributesResponse, Attribute } from '@features/products/graphql/types';
import { apolloContextForShop } from '@core/api/apolloContext';
import { CATALOG_WATCH_OPTIONS } from '@core/api/apolloQueryOptions';
import { fetchPerShop } from '@core/api/fetchPerShop';
import {
  readAttributesCache,
  writeAttributesCache,
} from '@core/cache/queryCache';
import { getEnvShopId, MULTI_SHOP_ENABLED } from '@core/config/shops';
import { useApolloClients } from '@core/context/ApolloClientsContext';
import { useShopIdsKey, parseShopIdsKey } from '@core/tenant/useShopIdsKey';

export interface AttributesContextValue {
  attributes: Attribute[];
  loading: boolean;
  error: Error | null;
}

const AttributesContext = createContext<AttributesContextValue | null>(null);

export function AttributesProvider({ children }: { children: ReactNode }) {
  const shopIdsKey = useShopIdsKey();
  const shopIds = useMemo(() => parseShopIdsKey(shopIdsKey), [shopIdsKey]);
  const scopeId = getEnvShopId();
  const { tenantLoading } = useApolloClients();

  const [multiAttrs, setMultiAttrs] = useState<Attribute[]>([]);
  const [multiLoading, setMultiLoading] = useState(MULTI_SHOP_ENABLED);
  const [multiError, setMultiError] = useState<Error | null>(null);

  const { data, loading, error } = useQuery<GetAttributesResponse>(
    GET_ATTRIBUTES,
    {
      skip: MULTI_SHOP_ENABLED,
      context: apolloContextForShop(scopeId),
      ...CATALOG_WATCH_OPTIONS,
    }
  );

  const loadMultiShop = useCallback(async () => {
    if (!MULTI_SHOP_ENABLED || tenantLoading || !shopIds.length) {
      return;
    }

    const cached = readAttributesCache<Attribute[]>(shopIds);
    if (cached?.length) {
      setMultiAttrs(cached);
      setMultiLoading(false);
      setMultiError(null);
      return;
    }

    setMultiLoading(true);
    setMultiError(null);
    try {
      const results = await fetchPerShop<GetAttributesResponse>({
        shopIds,
        query: GET_ATTRIBUTES,
        fetchPolicy: 'cache-first',
      });

      const byKey = new Map<string, Attribute>();
      results.forEach(result => {
        if (result.status !== 'fulfilled' || !result.data) return;
        for (const a of result.data.mst_attributes ?? []) {
          const key = a.id || a.name;
          if (key) byKey.set(key, a);
        }
      });
      const merged = [...byKey.values()];
      setMultiAttrs(merged);
      if (merged.length) {
        writeAttributesCache(shopIds, merged);
      }
    } catch (e) {
      setMultiError(e instanceof Error ? e : new Error(String(e)));
      setMultiAttrs([]);
    } finally {
      setMultiLoading(false);
    }
  }, [shopIdsKey, tenantLoading, shopIds]);

  useEffect(() => {
    if (!MULTI_SHOP_ENABLED) return;
    if (tenantLoading || !shopIds.length) {
      setMultiAttrs([]);
      setMultiLoading(false);
      return;
    }
    void loadMultiShop();
  }, [shopIdsKey, tenantLoading, loadMultiShop]);

  const value = useMemo(
    (): AttributesContextValue => ({
      attributes: MULTI_SHOP_ENABLED
        ? multiAttrs
        : data?.mst_attributes || [],
      loading: MULTI_SHOP_ENABLED ? multiLoading : loading,
      error: MULTI_SHOP_ENABLED
        ? multiError
        : error
          ? error instanceof Error
            ? error
            : new Error('Failed to fetch attributes')
          : null,
    }),
    [multiAttrs, multiLoading, multiError, data?.mst_attributes, loading, error]
  );

  return (
    <AttributesContext.Provider value={value}>
      {children}
    </AttributesContext.Provider>
  );
}

export function useAttributesContext(): AttributesContextValue {
  const ctx = useContext(AttributesContext);
  if (!ctx) {
    throw new Error(
      'useAttributesContext must be used within AttributesProvider'
    );
  }
  return ctx;
}
